import { useNavigate } from 'react-router-dom';
import {
  COLOR_TIPO_EVENTO,
  ETIQUETAS_TIPO_EVENTO,
  formatearCLP,
  type EventoCalendario,
  type TipoEvento,
} from '@lexicore/shared';
import {
  IconoCerrar,
  IconoDerecha,
  IconoReloj,
} from '../../componentes/Iconos';

interface Props {
  evento: EventoCalendario;
  alCerrar: () => void;
}

export function ModalDetalleEventoCalendario({ evento, alCerrar }: Props) {
  const navegar = useNavigate();

  const colorTipo = COLOR_TIPO_EVENTO[evento.tipo as TipoEvento] || 'var(--accent-blue)';
  const etiquetaTipo = ETIQUETAS_TIPO_EVENTO[evento.tipo as TipoEvento] || evento.tipo;

  const esUrgente48h = evento.urgencia === 'URGENTE_48H';
  const esProximo7d = evento.urgencia === 'PROXIMO_7D';
  const esVencido = evento.urgencia === 'VENCIDO';

  const diasRestantes = evento.metadatos?.diasRestantes;

  const handleAccion = () => {
    alCerrar();
    const rutaSugerida = evento.metadatos?.accionSugerida?.ruta;
    if (rutaSugerida) {
      // Mapear rutas de contratos a las rutas del frontend
      if (rutaSugerida.startsWith('/gastos')) {
        navegar('/costos');
      } else if (rutaSugerida.startsWith('/facturas')) {
        navegar('/cobranza');
      } else if (rutaSugerida.startsWith('/licitaciones')) {
        navegar('/pipeline');
      } else {
        navegar(rutaSugerida);
      }
      return;
    }

    // Fallbacks por entidad
    switch (evento.entidad) {
      case 'factura':
        navegar('/cobranza');
        break;
      case 'gasto':
      case 'gasto_recurrente':
        navegar('/costos');
        break;
      case 'proyecto':
        navegar(`/proyectos/${evento.entidadId}`);
        break;
      case 'licitacion':
        navegar('/pipeline');
        break;
      default:
        break;
    }
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" style={{ maxWidth: 520, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        {/* Cabecera del Modal */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: colorTipo,
                flexShrink: 0,
              }}
            />
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                {etiquetaTipo}
              </h2>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {evento.categoriaFlujo === 'INGRESO'
                  ? 'Entrada de Caja Proyectada'
                  : evento.categoriaFlujo === 'EGRESO'
                  ? 'Salida de Tesorería / Compromiso'
                  : 'Hito u Operación de Proyecto'}
              </span>
            </div>
          </div>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
            <IconoCerrar size={15} />
          </button>
        </div>

        {/* Cuerpo con Detalles del Evento */}
        <div style={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Título Principal y Estado de Urgencia */}
          <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
              <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: 'var(--text-primary)', lineHeight: 1.35 }}>
                {evento.titulo}
              </h3>

              {esUrgente48h ? (
                <span
                  style={{
                    padding: '3px 8px',
                    borderRadius: 12,
                    background: 'var(--accent-red-soft)',
                    color: 'var(--accent-red)',
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <IconoReloj size={12} />
                  <span>Urgente 48h</span>
                </span>
              ) : esProximo7d ? (
                <span
                  style={{
                    padding: '3px 8px',
                    borderRadius: 12,
                    background: 'var(--accent-yellow-soft)',
                    color: 'var(--accent-yellow)',
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <IconoReloj size={12} />
                  <span>Próximos 7 días</span>
                </span>
              ) : esVencido ? (
                <span
                  style={{
                    padding: '3px 8px',
                    borderRadius: 12,
                    background: 'var(--accent-red-soft)',
                    color: 'var(--accent-red)',
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Vencido
                </span>
              ) : null}
            </div>

            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '6px 0 0 0', lineHeight: 1.45 }}>
              {evento.descripcion}
            </p>
          </div>

          {/* Tarjeta de Datos Clave (Fecha y Monto) */}
          <div
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '14px 16px',
              display: 'grid',
              gridTemplateColumns: evento.monto !== null ? '1fr 1fr' : '1fr',
              gap: 12,
            }}
          >
            <div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Fecha Programada</span>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-mono)',
                  marginTop: 2,
                }}
              >
                {evento.fecha.slice(0, 10)}
              </div>
              {diasRestantes !== undefined && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {diasRestantes === 0
                    ? 'Vence hoy'
                    : diasRestantes > 0
                    ? `En ${diasRestantes} día(s)`
                    : `Venció hace ${Math.abs(diasRestantes)} día(s)`}
                </div>
              )}
            </div>

            {evento.monto !== null && (
              <div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {evento.categoriaFlujo === 'INGRESO' ? 'Monto a Cobrar' : 'Monto a Pagar / Compromiso'}
                </span>
                <div
                  className="td-mono"
                  style={{
                    fontSize: 17,
                    fontWeight: 800,
                    marginTop: 2,
                    color:
                      evento.categoriaFlujo === 'INGRESO'
                        ? 'var(--accent-green)'
                        : evento.categoriaFlujo === 'EGRESO'
                        ? 'var(--accent-red)'
                        : 'var(--text-primary)',
                  }}
                >
                  {formatearCLP(evento.monto)}
                </div>
                {evento.metadatos?.saldoPendiente !== undefined && evento.metadatos.saldoPendiente !== evento.monto && (
                  <div style={{ fontSize: 11, color: 'var(--accent-yellow)', marginTop: 2 }}>
                    Saldo pendiente: {formatearCLP(evento.metadatos.saldoPendiente)}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Metadatos y Entidades Asociadas */}
          {evento.metadatos && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                fontSize: 12,
                color: 'var(--text-secondary)',
              }}
            >
              {evento.metadatos.organismoNombre && (
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>Organismo Comprador:</strong>{' '}
                  {evento.metadatos.organismoNombre}
                </div>
              )}

              {evento.metadatos.proveedorNombre && (
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>Proveedor:</strong>{' '}
                  {evento.metadatos.proveedorNombre}
                </div>
              )}

              {evento.metadatos.proyectoNombre && (
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>Proyecto:</strong>{' '}
                  {evento.metadatos.proyectoNombre}
                </div>
              )}

              {evento.metadatos.codigoMercadoPublico && (
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>ID Mercado Público:</strong>{' '}
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-blue)' }}>
                    {evento.metadatos.codigoMercadoPublico}
                  </span>
                </div>
              )}

              {evento.metadatos.numeroDocumento && (
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>N° Documento:</strong>{' '}
                  <span style={{ fontFamily: 'var(--font-mono)' }}>#{evento.metadatos.numeroDocumento}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pie del Modal con Acciones 1-Clic */}
        <div
          className="modal-footer"
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button type="button" className="btn btn-secondary" onClick={alCerrar}>
            Cerrar
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAccion}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background:
                evento.categoriaFlujo === 'INGRESO'
                  ? 'var(--accent-green)'
                  : evento.categoriaFlujo === 'EGRESO'
                  ? 'var(--accent-red)'
                  : 'var(--accent-blue)',
              borderColor:
                evento.categoriaFlujo === 'INGRESO'
                  ? 'var(--accent-green)'
                  : evento.categoriaFlujo === 'EGRESO'
                  ? 'var(--accent-red)'
                  : 'var(--accent-blue)',
            }}
          >
            <span>{evento.metadatos?.accionSugerida?.etiqueta ?? 'Ver Ficha Completa'}</span>
            <IconoDerecha size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
